import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import type { SummaryDto } from '@mnemo/shared';
import type { Locale, TranslationKey } from '../i18n.js';
import { ApiError, api } from '../lib/api.js';
import { ErrorState, Loading, EmptyState } from '../components/ui.js';
import { PeriodPicker, useDateRange } from '../components/DateRange.js';

type T = (key: TranslationKey, vars?: Record<string, string | number>) => string;

/**
 * La synthèse par établissement.
 *
 * C'est ce dont les écoles ont besoin au quotidien : des **volumes et des tendances**,
 * pas la liste des travaux un par un. Elle a aussi l'avantage de n'exposer aucune
 * personne — rien que des comptages.
 *
 * Elle partage sa période avec la vue des journaux (`?du=…&au=…`) : passer d'un onglet à
 * l'autre garde le contexte, et les deux URL restent partageables.
 *
 * Depuis le 24.09.2026, elle compte les travaux par établissement de l'IMPRIMANTE (D14), et
 * les répartit par centre de coût (SYN-05) — ce dont les écoles ont besoin pour refacturer.
 */

/** Tendance mensuelle, en barres. Décorative : le tableau porte déjà les chiffres. */
function Sparkline({ values, label }: { values: number[]; label: string }) {
  if (values.length === 0) return null;
  const max = Math.max(...values, 1);
  return (
    <span className="inline-flex h-6 items-end gap-[2px]" role="img" aria-label={label}>
      {values.map((v, i) => (
        <span
          key={i}
          aria-hidden="true"
          className="w-[5px] rounded-sm bg-accent/70"
          style={{ height: `${Math.max(2, Math.round((v / max) * 24))}px` }}
        />
      ))}
    </span>
  );
}

export function Summary({ t, locale }: { t: T; locale: Locale }) {
  const [params, setParams] = useSearchParams();
  const [summary, setSummary] = useState<SummaryDto | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [errorCode, setErrorCode] = useState('INTERNAL');

  const numberFormat = useMemo(
    () => new Intl.NumberFormat(locale === 'de' ? 'de-CH' : 'fr-CH'),
    [locale],
  );

  const dates = useDateRange(params, setParams);

  const load = useCallback(async () => {
    setStatus('loading');
    try {
      const p = new URLSearchParams();
      if (dates.from) p.set('from', dates.from);
      if (dates.to) p.set('to', dates.to);
      setSummary(await api.summary(p));
      setStatus('ready');
    } catch (err) {
      setErrorCode(err instanceof ApiError ? err.code : 'INTERNAL');
      setStatus('error');
    }
  }, [dates.from, dates.to]);

  useEffect(() => {
    void load();
  }, [load]);

  /** Le lien vers les travaux d'un centre de coût, sur la même période. */
  const jobsOf = (establishment: string, costCenter: string) => {
    const p = new URLSearchParams();
    if (dates.from) p.set('du', dates.from);
    if (dates.to) p.set('au', dates.to);
    p.set('etab', establishment);
    p.set('cc', costCenter);
    return { pathname: '/', search: `?${p.toString()}` };
  };

  const share = (value: number, total: number) =>
    total > 0 ? Math.round((value / total) * 100) : 0;

  return (
    <div className="mx-auto flex max-w-7xl flex-col gap-4 p-4">
      <section className="card flex flex-wrap items-center gap-4 p-2.5 pl-3">
        <PeriodPicker
          t={t}
          locale={locale}
          from={dates.from}
          to={dates.to}
          onChange={dates.setRange}
        />

        {summary && status === 'ready' && (
          <dl className="ml-auto flex flex-wrap gap-6 text-sm">
            <div>
              <dt className="text-muted">{t('summary.jobs')}</dt>
              <dd className="text-xl font-semibold tabular-nums">
                {numberFormat.format(summary.totals.jobs)}
              </dd>
            </div>
            <div>
              <dt className="text-muted">{t('summary.pages')}</dt>
              <dd className="text-xl font-semibold tabular-nums">
                {numberFormat.format(summary.totals.pages)}
              </dd>
            </div>
            <div>
              <dt className="text-muted">{t('summary.color')}</dt>
              <dd className="text-xl font-semibold tabular-nums">
                {numberFormat.format(summary.totals.pagesColor)}
                <span className="ml-1 text-sm font-normal text-muted">
                  ({share(summary.totals.pagesColor, summary.totals.pages)} %)
                </span>
              </dd>
            </div>
          </dl>
        )}
      </section>

      <section className="card overflow-hidden">
        {status === 'error' && <ErrorState t={t} code={errorCode} onRetry={() => void load()} />}
        {status === 'loading' && !summary && <Loading t={t} />}
        {status === 'ready' && summary?.items.length === 0 && (
          <EmptyState message={t('summary.empty')} />
        )}

        {summary && summary.items.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-bg text-left text-xs uppercase tracking-wide text-muted">
                <tr>
                  <th scope="col" className="px-3 py-2">
                    {t('table.establishment')}
                  </th>
                  <th scope="col" className="px-3 py-2 text-right">
                    {t('summary.jobs')}
                  </th>
                  <th scope="col" className="px-3 py-2 text-right">
                    {t('summary.pages')}
                  </th>
                  <th scope="col" className="px-3 py-2 text-right">
                    {t('summary.color')}
                  </th>
                  <th scope="col" className="hidden px-3 py-2 text-right sm:table-cell">
                    {t('summary.accounts')}
                  </th>
                  <th scope="col" className="hidden px-3 py-2 text-right sm:table-cell">
                    {t('summary.devices')}
                  </th>
                  <th scope="col" className="hidden px-3 py-2 md:table-cell">
                    {t('summary.trend')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {summary.items.map((item) => (
                  <tr key={item.code} className="border-t border-border">
                    <td className="px-3 py-2">
                      <span className="chip bg-accent/15 text-fg">{item.code}</span>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {numberFormat.format(item.jobs)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {numberFormat.format(item.pages)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {numberFormat.format(item.pagesColor)}
                      {/* Jamais la couleur seule : le pourcentage est écrit (Charte §7). */}
                      <span className="ml-1 text-xs text-muted">
                        {share(item.pagesColor, item.pages)} %
                      </span>
                    </td>
                    <td className="hidden px-3 py-2 text-right tabular-nums sm:table-cell">
                      {numberFormat.format(item.users)}
                    </td>
                    <td className="hidden px-3 py-2 text-right tabular-nums sm:table-cell">
                      {numberFormat.format(item.devices)}
                    </td>
                    <td className="hidden px-3 py-2 md:table-cell">
                      <Sparkline
                        values={item.monthly.map((m) => m.pages)}
                        label={t('summary.trendLabel', {
                          establishment: item.code,
                          months: item.monthly.length,
                        })}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {summary && summary.items.length > 1 && (
        <p className="px-1 text-xs text-muted">{t('summary.accountsNote')}</p>
      )}

      {summary && status === 'ready' && summary.costCenters.length > 0 && (
        <section className="card overflow-hidden">
          <div className="border-b border-border px-3 py-2">
            <h2 className="font-semibold">{t('summary.costCenters')}</h2>
            <p className="text-xs text-muted">{t('summary.costCentersHint')}</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-bg text-left text-xs uppercase tracking-wide text-muted">
                <tr>
                  <th scope="col" className="px-3 py-2">
                    {t('table.establishment')}
                  </th>
                  <th scope="col" className="px-3 py-2">
                    {t('table.costCenter')}
                  </th>
                  <th scope="col" className="px-3 py-2 text-right">
                    {t('summary.jobs')}
                  </th>
                  <th scope="col" className="px-3 py-2 text-right">
                    {t('summary.pages')}
                  </th>
                  <th scope="col" className="hidden px-3 py-2 text-right sm:table-cell">
                    {t('summary.color')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {summary.costCenters.map((cc, i) => {
                  // L'établissement n'est écrit qu'à sa première ligne : la table se lit
                  // par blocs, comme une facture.
                  const first =
                    i === 0 || summary.costCenters[i - 1]!.establishment !== cc.establishment;
                  return (
                    <tr
                      key={`${cc.establishment}|${cc.name ?? ''}`}
                      className={first ? 'border-t border-border' : ''}
                    >
                      <td className="px-3 py-1.5">
                        {first && (
                          <span className="chip bg-accent/15 text-fg">{cc.establishment}</span>
                        )}
                      </td>
                      <td className="px-3 py-1.5">
                        {cc.name ? (
                          <Link
                            to={jobsOf(cc.establishment, cc.name)}
                            title={t('summary.costCenterOpen')}
                            className="underline-offset-2 hover:text-accent hover:underline"
                          >
                            {cc.name}
                          </Link>
                        ) : (
                          <span className="text-muted">{t('summary.costCenterNone')}</span>
                        )}
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums">
                        {numberFormat.format(cc.jobs)}
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums">
                        {numberFormat.format(cc.pages)}
                      </td>
                      <td className="hidden px-3 py-1.5 text-right tabular-nums sm:table-cell">
                        {numberFormat.format(cc.pagesColor)}
                        <span className="ml-1 text-xs text-muted">
                          {share(cc.pagesColor, cc.pages)} %
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
